import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useCallback, useEffect, useRef } from 'react';
import { useWindowDimensions } from 'react-native';
import { twJoin } from 'tailwind-merge';

import {
  AskTheLibraryEntry,
  useAskTheLibraryEntryVisible,
} from '@/components/libraryChat/AskTheLibraryEntry';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';

/** Tailwind's `lg`, which is where the page grows a right column on web. */
export const LIBRARY_RAIL_MIN_WIDTH = 1024;

export const LIBRARY_RAIL_WIDTH_CLASS = 'w-[312px]';

/**
 * Whether the right column is drawn. It holds only the Research card, so it exists on web at
 * `lg` and wider for a reader who can see that card, and never in the Saved view. Everyone else
 * gets the full-width main column rather than an empty gutter. `showResearchInline` says the
 * Research card stays in its place under the search, which is everywhere the rail is not.
 */
export function useLibraryRail({
  showSavedPosts,
  canReadResearch,
  isResolvingAbility = false,
}: {
  showSavedPosts: boolean;
  /** From `ResearchAbilityProbe`, since the visibility hook cannot read the ability. */
  canReadResearch: boolean;
  /**
   * The signed-in reader's account is still loading, so their ability is not known yet. The
   * column's room is held meanwhile, so the page does not shift when the card arrives.
   */
  isResolvingAbility?: boolean;
}) {
  const { width } = useWindowDimensions();

  const researchAvailable = useAskTheLibraryEntryVisible();

  const hasResearch = researchAvailable && canReadResearch && !showSavedPosts;

  const isWide = isWeb && width >= LIBRARY_RAIL_MIN_WIDTH;

  const showRail = isWide && hasResearch;

  // Held for an eligible reader whose ability is still loading, and drawn empty. A reader
  // without the ability gives the room back once it resolves, which is the rare case.
  const reservesRail =
    showRail ||
    (isWide && researchAvailable && isResolvingAbility && !canReadResearch && !showSavedPosts);

  return {
    isWide,
    showRail,
    reservesRail,
    showResearchInline: !reservesRail && !showSavedPosts,
  };
}

/** Space kept above the pinned card. */
const PIN_TOP = 24;

/**
 * The anchor starts directly under the tab bar, so its top is the bottom edge of the tab
 * underline (the tabs' `border-b`, 1px). The card's own 1px top border has to land on that line,
 * not under it, so the card starts one pixel above the anchor and the two lines read as one.
 */
const TAB_UNDERLINE = 1;

/** The gap between the main column's right edge and the rail's left edge. */
const RAIL_GAP = 32;

/**
 * Keeps the rail in view while the list scrolls. The list is the scroll container, and a sticky
 * child of its header would let go as soon as the header scrolled out, so the rail is not in the
 * list at all: it is positioned over the list's area and moved with the scroll. Its top is
 * the bottom edge of the tab bar, so the card's top border sits on the tab underline, and never
 * less than `PIN_TOP`, so it stops 24px below the top of the scroll area. When the card and that offset do not fit the area, it
 * is not pinned and scrolls away with the page like any other content. Web only, so it works on
 * the DOM nodes directly instead of rendering on every scroll event.
 */
export function usePinnedRail(enabled: boolean) {
  const overlayRef = useRef<any>(null);

  const anchorRef = useRef<any>(null);

  const railRef = useRef<any>(null);

  const update = useCallback(() => {
    const overlay = overlayRef.current as HTMLElement | null;

    const anchor = anchorRef.current as HTMLElement | null;

    const rail = railRef.current as HTMLElement | null;

    if (!overlay?.getBoundingClientRect || !anchor?.getBoundingClientRect || !rail) {
      return;
    }

    const bounds = overlay.getBoundingClientRect();

    const level = anchor.getBoundingClientRect().top - bounds.top - TAB_UNDERLINE;

    const fits = rail.offsetHeight + PIN_TOP * 2 <= bounds.height;

    // Left comes from the main column's measured right edge, so the gap is exact whatever the
    // viewport, scrollbar or centering does to the grid.
    rail.style.left = `${anchor.getBoundingClientRect().right - bounds.left + RAIL_GAP}px`;

    rail.style.transform = `translateY(${fits ? Math.max(PIN_TOP, level) : level}px)`;
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    window.addEventListener('resize', update);

    return () => window.removeEventListener('resize', update);
  }, [enabled, update]);

  // The header can change height between renders, such as when the announcement arrives.
  useEffect(() => {
    if (enabled) {
      update();
    }
  });

  return { overlayRef, anchorRef, railRef, update };
}

/**
 * The rail itself, positioned over the list's right edge and nothing else. It is exactly the
 * card's column wide, so no wrapper spans the toolbar or the posts and takes their clicks or
 * their scroll wheel. `usePinnedRail` sets its left, 32px past the main column, and moves it
 * vertically.
 */
export function PinnedLibraryRail({ railRef }: Pick<ReturnType<typeof usePinnedRail>, 'railRef'>) {
  return (
    <View ref={railRef} className={twJoin(LIBRARY_RAIL_WIDTH_CLASS, 'absolute left-0 top-0')}>
      <LibraryRail />
    </View>
  );
}

export function LibraryRail({ className }: { className?: string }) {
  return (
    <VStack
      className={twJoin(LIBRARY_RAIL_WIDTH_CLASS, 'shrink-0 gap-4', className)}
      accessibilityLabel="Research">
      <AskTheLibraryEntry fullWidth />
    </VStack>
  );
}
